import { activeCart } from '@/services/checkout';
import { setCookie } from 'cookies-next';
import { getCustomerCity } from '@/services/user/addresses';

const EXCLUDED_PATHS = [
  '/checkout',
  '/login',
  '/register',
  '/forgot-password',
  '/success',
];

export function decodeJwt(token, fromServer = true) {
  if (!token) {
    return;
  }
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  if (!fromServer) {
    return JSON.parse(window.atob(base64));
  } else {
    return JSON.parse(Buffer.from(base64, 'base64').toString());
  }
}

export function handleFooterWithCategories(footerData) {
  return footerData?.map((item, index) => {
    // If the item already has all required properties, return as is
    if (item?.active !== undefined && item?.position !== undefined) {
      return item;
    }

    // For items with title properties, add positioning and activation
    if (item?.['title-ar'] && item?.['title-en']) {
      const newItem = {
        ...item,
        active: true,
        'mobile-active': 'true',
        'mobile-position': String(index + 1),
        position: String(index + 1),
      };

      // Transform the value array if it exists
      if (item?.value && Array.isArray(item?.value)) {
        newItem.value = item?.value?.map((subItem, subIndex) => ({
          ...subItem,
          active: true,
          'mobile-active': 'true',
          'mobile-position': String(subIndex + 1),
          position: String(subIndex + 1),
        }));
      }

      return newItem;
    }

    // Return item as is if it doesn't match expected structure
    return item;
  });
}

export const sortJSONKeys = (values, isMobile) =>
  values?.some(
    (item) =>
      (!isMobile ? item?.active : item?.['mobile-active'] || item?.active) &&
      'position' in item
  )
    ? values?.sort((a, b) => Number(a?.position) - Number(b?.position))
    : values;

export function formatDate(dateString) {
  const date = new Date(dateString);

  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const time = date.toLocaleTimeString('en-US', options);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });

  return `${time.toLowerCase()}, ${day} ${month}`;
}

export function formatDateString(input, lang) {
  // Convert input to ISO format
  const date = new Date(input.replace(' ', 'T'));

  // Format date as "DD Mon YYYY"
  return new Intl.DateTimeFormat(lang == 'en' ? 'en-GB' : 'ar-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function findObjectByIdV2(data, targetKey, searchValue) {
  if (data && Array.isArray(data) && data?.length) {
    for (const item of data) {
      if (item[targetKey] == searchValue) {
        // prettyConole(searchValue, item)
        return item;
      }

      if (item?.children_data && item?.children_data.length > 0) {
        const result = findObjectByIdV2(
          item?.children_data,
          targetKey,
          searchValue
        );
        if (result) {
          // prettyConole(searchValue, result)
          return result;
        }
      }
    }
  }
  // Target object not found
  return null;
}

export function clearFalsyKeysFromObject(obj) {
  const filteredObj = {};

  for (const key in obj) {
    const value = obj[key];

    if (value === null || value === undefined) continue;

    if (typeof value === 'string' && value.trim() === '') continue;

    filteredObj[key] = value;
  }

  return filteredObj;
}

export const getFullImagePath = (path, fromCategories = false) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  if (path.startsWith('/')) {
    path = path.slice(1);
  }

  if (fromCategories && !path.startsWith('media')) {
    path = `media/catalog/category/${path}`;
  }

  const imagePath = `${process.env.NEXT_PUBLIC_IMG_BASEURL}/${path}`;

  return imagePath;
};

export function findObjectById(data, targetKey, searchValue) {
  if (data?.length) {
    for (const item of data) {
      if (item[targetKey] == searchValue) {
        return item;
      }

      if (item?.children_data && item?.children_data.length > 0) {
        const result = findObjectById(
          item?.children_data,
          targetKey,
          searchValue
        );
        if (result) {
          return result;
        }
      }
    }
  }
  return null;
}

export const modifyMenuTreeData = (
  array,
  parentURL = '',
  parentBreadcrumb = []
) => {
  return (array || [])
    .filter(
      (cat) =>
        cat.is_active && cat.include_in_menu === '1' && cat.product_count > 0
    )
    .map((cat) => {
      // Set currentURL for each category
      const currentURL = `${parentURL}/${cat.innova_url}`;

      // Build up the breadcrumb path
      const breadcrumb = [
        ...parentBreadcrumb,
        {
          name: cat.name,
          url: currentURL,
        },
      ];

      const modifiedCategory = {
        ...cat,
        currentURL,
        breadcrumb,
      };

      // If there are children, apply recursion to include them
      if (cat.children_data?.length) {
        modifiedCategory.children_data = modifyMenuTreeData(
          cat.children_data,
          currentURL,
          breadcrumb
        );
      }

      return modifiedCategory;
    });
};

export const handleLinkByType = (link, type) => {
  switch (type) {
    case 'url':
      return link;
    case 'product':
      return link?.startsWith('/product/') ? link : `/product/${link}`;
    case 'category':
      return link?.startsWith('/') ? link?.slice(0) : `/${link}`;
    default:
      return ``;
  }
};

export const handleMiddlewareSegments = (
  request,
  requiredLocale,
  currentLocale
) => {
  const url = request.nextUrl.clone();
  const { pathname } = request.nextUrl;

  const segments = pathname?.split('/')?.filter((segment) => segment !== '');

  if (segments.length >= 1) {
    url.pathname = `/${requiredLocale}/${segments[0].toLocaleLowerCase() === currentLocale ? segments.slice(1).join('/') : segments.join('/')}`;
  } else {
    url.pathname = `/${requiredLocale}`;
  }

  return url;
};

export const iconsClass = {
  width: '25px',
  height: '25px',
  backgroundImage: `url(https://maps.gstatic.com/mapfiles/embed/images/entity11.png)`,
  backgroundSize: 'auto',
  backgroundPosition: '189px 3px',
};

export const handleAddToActiveCart = async (order_id, push, mage) => {
  const res = await activeCart(order_id);
  const cart_id = res?.data?.data?.cart_id;

  if (cart_id) {
    setCookie('quote', cart_id);
    // setTimeout(() => {
    mage ? push('/checkout') : push('/cart');
    // }, 200);
  }
};

export const getErrorMessage = (payment, reason, locale) => {
  if (payment === 'tabby') {
    if (reason === 'Failure') {
      return locale?.includes('en')
        ? 'Sorry, Tabby is unable to approve this purchase. Please use an alternative payment method for your order.'
        : 'نأسف، تابي غير قادرة على الموافقة على هذه العملية. الرجاء استخدام طريقة دفع أخرى.';
    }
    return locale?.includes('en')
      ? 'You aborted the payment. Please retry or choose another payment method.'
      : 'لقد ألغيت الدفعة. فضلاً حاول مجددًا أو اختر طريقة دفع أخرى.';
  }
  return locale?.includes('en')
    ? 'Payment Failed, Please try again'
    : 'فشلت عملية الدفع, برجاء المحاولة مره اخرى';
};

export function unescapeHtml(escapedHtml) {
  const htmlEntities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
  };

  return escapedHtml
    ?.replace(/&amp;|&lt;|&gt;|&quot;|&#39;/g, (match) => htmlEntities[match])
    ?.replaceAll('"{{media url="', `${process.env.NEXT_PUBLIC_IMG_BASEURL}/`)
    ?.replaceAll(`/media/" `, '/media/')
    ?.replaceAll('"}}"', '')
    ?.replaceAll(
      '{{media url=',
      `${process.env.NEXT_PUBLIC_IMG_BASEURL}/media/`
    )
    ?.replaceAll('}}', '');
}

export function checkStockAvailability(stockInnova, sourceIds) {
  return sourceIds.some((id) => {
    const quantityStr = stockInnova[id];
    const quantity = parseFloat(quantityStr);
    return quantity > 0;
  });
}

export const getRegionIDWithLat = async (lat, long) => {
  const response = await getCustomerCity({
    lat,
    long,
  });

  return response?.data?.data;
};

export const generatePaginationPageNumbers = (
  currentPage,
  pageCount,
  maxPagesToShow = 6
) => {
  const pages = [];

  pages.push(1);

  const start = Math.max(2, currentPage - Math.floor(maxPagesToShow / 2));
  const end = Math.min(
    pageCount - 1,
    currentPage + Math.floor(maxPagesToShow / 2)
  );

  if (start > 2) pages.push('...');

  for (let i = start; i <= end; i++) pages.push(i);

  if (end < pageCount - 1) pages.push('...');

  if (pageCount > 1) pages.push(pageCount);

  return pages;
};

export const FilterAndSortJson = (data, position, active) => {
  return (
    data
      ?.sort((a, b) => +a[position] - +b[position])
      ?.filter((tab) => tab[active])
      ?.map((item) => ({
        ...item,
        value: item?.value
          ?.filter((v) => v?.active)
          ?.sort((a, b) => +a?.[position] - +b?.[position]),
      })) || []
  );
};

export const checkSearchLang = (word, locale) => {
  const hasEnglish = /[a-zA-Z]/.test(word);
  const hasArabic = /[\u0600-\u06FF]/.test(word);

  if (hasEnglish && !hasArabic) {
    return 'en';
  } else if (hasArabic) {
    return 'ar';
  }
  return locale;
};

export const getSortOptions = (lang, t) => {
  const sortIndex = {
    ar: {
      Recommended: process.env.NEXT_PUBLIC_ALGOLIA_IDX_AR,
      LowestPrice: process.env.NEXT_PUBLIC_ALGOLIA_ASC_AR,
      HighestPrice: process.env.NEXT_PUBLIC_ALGOLIA_DESC_AR,
      HighestDiscount: process.env.NEXT_PUBLIC_ALGOLIA_Discount_DESC_AR,
    },
    en: {
      Recommended: process.env.NEXT_PUBLIC_ALGOLIA_IDX_EN,
      LowestPrice: process.env.NEXT_PUBLIC_ALGOLIA_ASC_EN,
      HighestPrice: process.env.NEXT_PUBLIC_ALGOLIA_DESC_EN,
      HighestDiscount: process.env.NEXT_PUBLIC_ALGOLIA_Discount_DESC_EN,
    },
  };

  return Object.entries(sortIndex[lang])?.map(([type, value]) => ({
    label: t(type),
    value,
  }));
};

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const getNeededProductData = (product) => {
  const productData = {
    prices_with_tax: product?.prices_with_tax,
    name: product?.name,
    innova_url: product.innova_url,
    entity_id: product?.entity_id,
    image: product?.image,
    sku: product.sku,
  };

  return productData;
};

export const shouldShowMainLayout = (pathname = '') => {
  if (!pathname) return true;
  if (EXCLUDED_PATHS?.includes(pathname) || pathname?.includes('success')) {
    return false;
  }
  return true;
};

export const formatKSA = (date) => {
  const utcDate = new Date(date?.replace(' ', 'T') + 'Z');

  const ksaString = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Riyadh',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })?.format(utcDate);

  const [day, month, yearAndTime] = ksaString?.split('/');
  const [year, time] = yearAndTime?.split(', ');

  return `${day}-${month}-${year} ${time}`;
};
